import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, type ButtonProps } from '@time-line/components/common';
import { useAddItem } from 'hooks/cart/useAddItem';
import { useCart } from 'hooks/cart/useCart';
import { useCartUI } from 'hooks/cart/useCartUI';
import { useDiscounts } from 'hooks/cart/useDiscounts';
import { useShopifyProductByMerchandiseIds } from 'hooks/product/useShopifyProductByMerchandiseIds';
import { useAnalytics } from 'hooks/useAnalytics';
import { useOneClickCheckout } from 'hooks/useOneClickCheckout';
import type { CustomAttributes } from 'types/cart/CustomAttributes';
import type { PromoCode } from 'types/cart/PromoCode';
import type { CTAType } from 'types/cms/product/CTAType';
import { PurchaseType } from 'types/product/PurchaseType';

import { DiscountNotice } from './DiscountNotice';
import { getCartItemBySelectedItem } from './utils/getCartItemBySelectedItem';

interface ProductAddToCartButtonProps extends ButtonProps {
  attributes?: CustomAttributes;
  promoCodes?: PromoCode[];
  merchandiseId: string;
  onAdded?: () => void;
  quantity?: number;
  sellingPlanId?: string;
  ctaType?: CTAType;
  purchaseType?: PurchaseType;
  showCart?: boolean;
  isPreorder?: boolean;
  isDiscountHidden?: boolean;
}

export function ProductAddToCartButton({
  attributes,
  merchandiseId,
  sellingPlanId,
  onAdded,
  quantity = 1,
  promoCodes,
  onClick,
  isLoading,
  ctaType,
  showCart = true,
  isPreorder,
  purchaseType,
  isDiscountHidden,
  ...props
}: ProductAddToCartButtonProps) {
  const queryClient = useQueryClient();
  const { data: shopifyProducts } = useShopifyProductByMerchandiseIds([
    merchandiseId,
  ]);
  const product = shopifyProducts?.[0];
  const { addDiscounts, isPending: isAddingDiscount } =
    useDiscounts(queryClient);
  const { mutate: addItem, isPending: isAddingProduct } =
    useAddItem(queryClient);
  const { addError, toggleCart } = useCartUI();
  const { data: cart } = useCart();

  const { analytics } = useAnalytics();

  const discountCodes = promoCodes?.map(({ code }) => code) || [];

  const handleAddItem = () => {
    addItem(
      {
        merchandiseId,
        sellingPlanId,
        quantity,
        attributes,
      },
      {
        onSuccess: () => {
          if (onAdded) onAdded();
          if (!promoCodes || promoCodes.length === 0)
            return toggleCart(showCart);
          return addDiscounts(discountCodes, {
            onSettled: () => toggleCart(showCart),
          });
        },
        onError: () => {
          const cartItem = getCartItemBySelectedItem({
            cart,
            variantId: merchandiseId,
            subscriptionId: sellingPlanId,
          });

          if (cartItem?.id) {
            addError({
              id: cartItem.id,
              message: 'Quantity could not be updated',
              errorRemovalTimeout: 6000,
            });
          }
          toggleCart(showCart);
        },
      },
    );
    if (product) {
      analytics.addToCart([
        {
          product,
          merchandiseId,
          quantity: 1,
        },
      ]);
    }
  };

  const { goToOneClickCheckout, isPending: isCheckoutPending } =
    useOneClickCheckout({
      lines: [
        {
          merchandiseId,
          sellingPlanId,
          quantity,
          attributes,
        },
      ],
      discountCodes,
    });

  if (ctaType === 'none') {
    return null;
  }

  if (ctaType === 'checkout') {
    const checkoutText =
      purchaseType === PurchaseType.SUB && sellingPlanId
        ? 'Subscribe Now'
        : 'Buy Now';

    return (
      <Flex flexDir="column" gap="0">
        {!isDiscountHidden && discountCodes && discountCodes.length > 0 && (
          <DiscountNotice discountCodes={discountCodes} />
        )}
        <Button
          className="button--checkout"
          isLoading={isCheckoutPending}
          onClick={goToOneClickCheckout}
          {...props}
        >
          {isPreorder ? 'Pre-order' : checkoutText}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="0">
      {!isDiscountHidden && discountCodes && discountCodes.length > 0 && (
        <DiscountNotice discountCodes={discountCodes} />
      )}
      <Button
        className="button--add-to-cart"
        isLoading={isLoading || isAddingProduct || isAddingDiscount}
        onClick={onClick || handleAddItem}
        variant="primary"
        {...props}
      >
        {props.children || (isPreorder ? 'Pre-order' : 'Add to cart')}
      </Button>
    </Flex>
  );
}
